/**
 * `useAnchoredPosition` — the ONE implementation of "a floating panel pinned to
 * a trigger", for every ⋯ menu, dropdown and popover in the app.
 *
 * THE CONVENTION IT ENCODES: **follow the anchor, dismiss when the anchor
 * leaves.** A panel tracks its trigger through scroll and resize, and the
 * moment that trigger is no longer visible the panel closes rather than
 * drifting to a spot it no longer describes.
 *
 * Before this, the app had two hand-rolled camps that disagreed:
 *   · CLOSE on any scroll — ProjectCard's ⋮, SharedView, ChatHomeView.
 *   · REPOSITION forever — ModelSelector, Tooltip, ToolbarPopover,
 *     ContextIndicator, FeaturePricingAdmin.
 * Both are defensible in isolation and neither is what we want everywhere: the
 * first closes a menu the user can still see, the second keeps one alive after
 * its trigger has gone. This hook is the third behaviour, and the only one that
 * is never wrong about what the panel is attached to.
 *
 * WHY EXIT IS AN IntersectionObserver AND NOT A RECT TEST. The obvious
 * implementation — "is the anchor's rect inside the viewport?" — is WRONG here
 * and fails on the exact case that motivated the hook. A drive row scrolled out
 * of the file list is clipped by the list's `overflow-auto` box, but its
 * `getBoundingClientRect()` can still sit happily inside the viewport, so a
 * rect test reports the anchor as visible while the user cannot see it at all.
 * IntersectionObserver computes the intersection through the containing-block
 * chain, so it accounts for clipping by every scrolling ancestor, which is the
 * question actually being asked.
 *
 * WHY WRITES ARE GUARDED. The state is a fresh object each pass, so React
 * re-renders on every write and re-runs anything keyed on the position. Writing
 * unconditionally on a scroll event is a render per frame; writing a value that
 * fails its own condition is an infinite loop (see the ⋯ menu clamp in
 * `DriveView` — it shipped, and a spacing tweak away from crashing the deck).
 * `samePos` makes both impossible: equal numbers, no write.
 */
import { useState, useEffect, useCallback, useLayoutEffect, useRef, type RefObject } from 'react'

export interface AnchoredPosition {
  top: number
  left: number
}

export interface UseAnchoredPositionOptions {
  /** Whether the panel is open. Everything is inert while false. */
  open: boolean
  /** The trigger the panel is pinned to. */
  anchorRef: RefObject<HTMLElement | null>
  /** The panel itself — measured so it can be flipped and clamped. */
  floatingRef: RefObject<HTMLElement | null>
  /**
   * Called when the anchor scrolls out of sight. Omit to opt out of the
   * dismiss half (a panel whose anchor cannot leave, e.g. a fixed toolbar).
   */
  onDismiss?: () => void
  /** `start` aligns the left edges, `end` aligns the right edges. */
  align?: 'start' | 'end'
  /** Space between anchor and panel. */
  gap?: number
  /** Minimum distance kept from every viewport edge. */
  margin?: number
  /**
   * How long the panel stays mounted, flagged `exiting`, after its anchor
   * leaves — so the consumer can play it out instead of having it blink away.
   *
   * This is NOT decoration. An anchor-exit dismissal is the one close the user
   * did not ask for, so vanishing instantly is indistinguishable from a crash
   * or a mis-click; a brief fade is what says "your row scrolled away" rather
   * than "something broke". Outside-click and Escape stay instant, because
   * there the user already knows why it closed.
   *
   * `0` dismisses immediately. Keep it in step with the CSS duration — the
   * timer is what unmounts, so a shorter timer truncates the animation.
   */
  exitTransitionMs?: number
  /**
   * Derive the rect the panel is POSITIONED against, from the element being
   * OBSERVED. Default: the element's own rect.
   *
   * The two are not always the same thing. A right-click menu belongs at the
   * pointer, but the thing that can scroll away — and so decides when to
   * follow and when to dismiss — is the row under it. Passing a zero-size rect
   * at the click point, expressed as an offset inside the row, keeps the menu
   * where the user clicked while it tracks and exits with the row.
   */
  anchorRect?: (el: HTMLElement) => { top: number; left: number; right: number; bottom: number; width: number; height: number }
  /**
   * Treat ANY scroll as an exit, instead of waiting for the anchor to leave.
   *
   * For a panel whose anchor cannot meaningfully scroll away but whose position
   * is only about where the pointer was — a right-click on empty space, pinned
   * to a point rather than to a row — the anchor never exits, so the exit rule
   * never fires and the menu sits there indefinitely. Scrolling is what makes
   * such a panel stale, so scrolling is what should close it.
   *
   * It stages through the same `exiting` flag, so the close animates exactly
   * like an anchor exit rather than being a second, differently-behaved kind of
   * dismissal.
   */
  exitOnScroll?: boolean
  /**
   * Which side of the anchor to prefer. `auto` (default) puts the panel below
   * and flips above only when below cannot hold it; `top`/`bottom` pin the
   * side. Pin it when the design depends on the side — a model picker that
   * always opens upward out of a composer reads as broken if it flips down over
   * the thing it belongs to.
   */
  placement?: 'auto' | 'top' | 'bottom'
  /**
   * CSS selector for scroll containers whose scrolling must NOT count as an
   * exit under {@link exitOnScroll}.
   *
   * A panel with its own overflowing body — or a nested child panel — produces
   * scroll events that are the user reading THIS panel, not leaving it. Without
   * the exception a long menu cannot be scrolled at all (the first wheel tick
   * closes it) and scrolling a child menu collapses its parent. That is a fixed
   * bug in `ToolbarPopover`; it is preserved here rather than rediscovered.
   */
  ignoreScrollWithin?: string
  /**
   * Identity of the CURRENT anchor, when one ref is repointed at different
   * elements over the panel's life — a per-row ⋯ button in a list, where the
   * open menu moves from card A to card B without closing.
   *
   * React sees only the ref OBJECT, whose identity never changes, so nothing
   * here re-measures or re-observes on a swap: the panel stays parked beside
   * A, and the exit observer keeps watching A, while the caller renders B's
   * actions — including destructive ones. Pass anything that changes with the
   * anchor (the row id) and both effects re-run.
   */
  anchorKey?: string | number | null
}

export interface AnchoredPositionState {
  /** `null` until the panel has been measured. */
  pos: AnchoredPosition | null
  /** True while playing out an anchor-exit dismissal. */
  exiting: boolean
  /**
   * The anchor's centre in the panel's OWN coordinates, for `transform-origin`.
   *
   * Without it a scale animation grows and shrinks about the panel's middle,
   * which reads as the panel itself pulsing. Origin-at-the-anchor makes it
   * collapse toward the control it belongs to, so the animation states the
   * relationship instead of merely being motion. Values are frequently
   * negative — an anchor above the panel is at a negative local y — and that is
   * correct, not a bug to clamp away.
   */
  origin: { x: number; y: number } | null
}

function samePos(a: AnchoredPosition | null, b: AnchoredPosition | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.top === b.top && a.left === b.left
}

export function useAnchoredPosition({
  open,
  anchorRef,
  floatingRef,
  onDismiss,
  align = 'start',
  gap = 4,
  margin = 8,
  exitTransitionMs = 0,
  anchorRect,
  exitOnScroll = false,
  placement = 'auto',
  ignoreScrollWithin,
  anchorKey = null,
}: UseAnchoredPositionOptions): AnchoredPositionState {
  // Latest-ref for the dismiss callback, so the observer's lifetime is decided
  // by the ANCHOR and nothing else. Held in a dep array instead, an unmemoised
  // `onDismiss` — a plain arrow at the call site, the obvious way to write it —
  // is a new identity every render, and the observer is then torn down and
  // recreated on each one. Every current caller memoises, so this was latent
  // rather than broken; making it structural means a caller cannot get it
  // wrong. Same idiom as `onCloseRef` in ToolbarPopover.
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss
  // PRESENCE, not identity. The ref above deliberately hides the callback's
  // identity from the effects; it must not also hide whether one EXISTS.
  // Guarding on `onDismissRef.current` alone reads the right value but gives
  // the effects nothing to re-run on, so a panel that gains a callback while
  // open never installs an observer, and one that loses it mid-exit strands
  // itself (see the rollback below).
  const canDismiss = !!onDismiss

  const [pos, setPos] = useState<AnchoredPosition | null>(null)
  const [origin, setOrigin] = useState<{ x: number; y: number } | null>(null)
  const [exiting, setExiting] = useState(false)

  const measure = useCallback(() => {
    const anchor = anchorRef.current
    const floating = floatingRef.current
    if (!anchor || !floating) return
    const a = anchorRect ? anchorRect(anchor) : anchor.getBoundingClientRect()
    // The panel's LAYOUT size, not its rendered rect. `getBoundingClientRect()`
    // returns the TRANSFORMED box, and these panels commonly carry an entry
    // animation that scales them in (`ws-anim-modal-in`) — so every measurement
    // taken mid-animation reads a smaller height, and a panel placed ABOVE its
    // anchor is positioned for a size it is about to outgrow. It then settles
    // too low and overlaps the trigger, permanently, because the animation's
    // end changes nothing a ResizeObserver reports (the border box never moved).
    // `offsetWidth/Height` are transform-independent, which is exactly the
    // property wanted here. The ANCHOR still uses its rect: for the thing being
    // pointed AT, the on-screen position is the truth.
    const f = { width: floating.offsetWidth, height: floating.offsetHeight }

    // Below the anchor by default; flip above only when below does not fit AND
    // above genuinely has more room, so a panel taller than either side stays
    // put rather than oscillating between two bad placements.
    const roomBelow = window.innerHeight - a.bottom - gap - margin
    const roomAbove = a.top - gap - margin
    const placeAbove = placement === 'top' ? true
      : placement === 'bottom' ? false
      : f.height > roomBelow && roomAbove > roomBelow
    let top = placeAbove ? a.top - gap - f.height : a.bottom + gap

    let left = align === 'end' ? a.right - f.width : a.left

    // Clamp into the viewport. `Math.max(margin, …)` LAST so a panel larger
    // than the viewport pins to the top-left edge instead of a negative
    // coordinate — and note the clamp is idempotent, which is what lets the
    // guarded write below terminate.
    top = Math.max(margin, Math.min(top, window.innerHeight - f.height - margin))
    left = Math.max(margin, Math.min(left, window.innerWidth - f.width - margin))

    setPos(prev => (samePos(prev, { top, left }) ? prev : { top, left }))

    // Anchor centre, expressed inside the panel's box, for `transform-origin`.
    // Computed from the SAME rects as the placement above, so the origin can
    // never disagree with where the panel actually landed.
    const ox = Math.round(a.left + a.width / 2 - left)
    const oy = Math.round(a.top + a.height / 2 - top)
    setOrigin(prev => (prev && prev.x === ox && prev.y === oy ? prev : { x: ox, y: oy }))
  }, [anchorRef, floatingRef, align, gap, margin, anchorRect, placement, anchorKey])

  // Measure before paint so the panel never shows at its pre-measured guess.
  useLayoutEffect(() => {
    if (!open) { setPos(null); setOrigin(null); setExiting(false); return }
    measure()
  }, [open, measure])

  // Follow. Capture phase because the scroll that matters is usually an inner
  // container's and scroll events do not bubble; passive because this never
  // calls preventDefault and must not block scrolling.
  //
  // Frozen once `exiting`: the anchor is gone, so continuing to track it would
  // drag the panel across the screen during the very animation meant to say it
  // is leaving. It plays out where the user last saw it.
  useEffect(() => {
    if (!open || exiting) return
    const onMove = () => measure()
    window.addEventListener('scroll', onMove, { capture: true, passive: true })
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, { capture: true } as EventListenerOptions)
      window.removeEventListener('resize', onMove)
    }
  }, [open, exiting, measure])

  // Dismiss on exit. `threshold: 0` fires as soon as the anchor is not
  // intersecting at all — fully gone, not merely clipped at an edge, so a panel
  // is never yanked away while its trigger is still partly on screen.
  //
  // With `exitTransitionMs` the close is staged: this effect only FLAGS the
  // exit; the timer that unmounts lives in its own effect below. They must stay
  // separate — `exiting` is in this dep list (so a stream of scroll-driven
  // callbacks cannot re-enter), which means flipping the flag tears this effect
  // down, and a timer owned here would be cleared by its own cleanup before it
  // could ever fire. The panel then animated to `opacity: 0` and hung there,
  // mounted and invisible, swallowing pointer events until something else
  // closed it.
  useEffect(() => {
    if (!open || exiting || !canDismiss) return
    const anchor = anchorRef.current
    if (!anchor || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (e.isIntersecting) continue
          if (exitTransitionMs > 0) setExiting(true)
          else onDismissRef.current?.()
          return
        }
      },
      { threshold: 0 },
    )
    io.observe(anchor)
    return () => io.disconnect()
  }, [open, exiting, anchorRef, canDismiss, exitTransitionMs, anchorKey])

  // Re-measure when the PANEL'S OWN SIZE changes. Measuring once on open is
  // not enough: content can settle after the first paint — ModelSelector picks
  // its provider tab in an effect, growing 267px -> 278px — and a panel placed
  // ABOVE its anchor then keeps its old `top` and grows DOWNWARD, straight over
  // the trigger it belongs to. Found by opening it, not by any assertion; the
  // numbers agreed with each other the whole time, they were just stale.
  //
  // This cannot feed back: `measure` changes only position, never size, so a
  // reposition does not re-fire the observer — and `samePos` drops the write
  // when the numbers land the same anyway.
  useEffect(() => {
    if (!open || exiting) return
    const floating = floatingRef.current
    if (!floating || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => measure())
    ro.observe(floating)
    const anchor = anchorRef.current
    if (anchor) ro.observe(anchor)   // a trigger that reflows moves the panel too
    return () => ro.disconnect()
  }, [open, exiting, floatingRef, anchorRef, measure, anchorKey])

  // `exitOnScroll`: any scroll stales the panel, so treat it as an exit. Shares
  // the staged path above so the close animates identically — a second, instant
  // kind of dismissal would defeat the point of animating the first.
  useEffect(() => {
    if (!open || exiting || !canDismiss || !exitOnScroll) return
    const onScroll = (e: Event) => {
      // Scrolling THIS panel (or a child of it) is reading it, not leaving it.
      if (ignoreScrollWithin && (e.target as Element | null)?.closest?.(ignoreScrollWithin)) return
      if (exitTransitionMs > 0) setExiting(true)
      else onDismissRef.current?.()
    }
    window.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => window.removeEventListener('scroll', onScroll, { capture: true } as EventListenerOptions)
  }, [open, exiting, canDismiss, exitOnScroll, exitTransitionMs, ignoreScrollWithin])

  // The unmount half of a staged exit.
  useEffect(() => {
    if (!exiting) return
    // Lost the ability to dismiss MID-EXIT. Nothing can unmount the panel now,
    // so leaving the flag set strands it at `opacity: 0` — mounted, invisible,
    // and inert. That is the same stranded state the split timer effect was
    // written to prevent, reached through a different door. Roll the exit back
    // instead, which returns it to visible and interactive.
    if (!canDismiss) { setExiting(false); return }
    const timer = setTimeout(() => onDismissRef.current?.(), exitTransitionMs)
    return () => clearTimeout(timer)
  }, [exiting, canDismiss, exitTransitionMs])

  return { pos, exiting, origin }
}
